import React, {ComponentProps, useEffect} from 'react';
import {
  cancelAnimationFrame,
  requestAnimationFrame,
} from 'react-viewport-utils';
import {useResetForced404Page} from './context';
import {
  getScrollTopAnchorId,
  shouldForceActionPush,
  useHistory,
} from './useHistory';
// eslint-disable-next-line monorepo/no-internal-import
import {isElementFullyInViewport} from '@aboutyou/utils/src/isElementFullyInViewport';

export const DEFAULT_SCROLL_ANCHOR_ID = '@aboutyou/router::SCROLL_ANCHOR';

/**
 * Listens to events on the history and improves the scroll position based on them.
 */
export const HistoryEventHandler = () => {
  const history = useHistory();
  const resetForced404Page = useResetForced404Page();

  // eslint-disable-next-line local-rules/too-complex-useeffects -- TODO: Cleanup complex useEffect https://aboutyou.atlassian.net/browse/ASFE-7605
  useEffect(() => {
    let lastTickId: number | undefined;
    const removeListener = history.listen(
      (location, action) => {
        const handleScroll = () => {
          lastTickId = undefined;
          const anchorId = getScrollTopAnchorId(history.location);
          const scrollAnchor = anchorId
            ? document.getElementById(anchorId)
            : null;

          if (!scrollAnchor) {
            window.scrollTo(0, 0);
          } else if (!isElementFullyInViewport(scrollAnchor)) {
            scrollAnchor.scrollIntoView({
              block: 'start',
              inline: 'center',
            });
          }
        };

        if (lastTickId) {
          cancelAnimationFrame(lastTickId);
        }
        if (
          action === 'PUSH' ||
          (action === 'REPLACE' && shouldForceActionPush(location)) // treat REPLACE as PUSH when forced
        ) {
          lastTickId = requestAnimationFrame(handleScroll);
        }
        resetForced404Page();
      },
      {
        disableUiUpdates: true,
      },
    );
    return () => {
      if (lastTickId) {
        cancelAnimationFrame(lastTickId);
      }
      removeListener();
    };
  }, [history, resetForced404Page]);
  return null;
};

interface ScrollTopAnchorProps<
  C extends keyof JSX.IntrinsicElements | React.ComponentType<any>,
> {
  anchorRef?: React.RefObject<HTMLElement>;
  as: C;
}

/**
 * Can be placed somewhere on the page. If it exists, and a link with property `considerScrollTopAnchor`, the page will jump to this element instead of the top of the page.
 */
export const ScrollTopAnchor = <
  C extends keyof JSX.IntrinsicElements | React.ComponentType<any>,
>({
  anchorId,
  anchorRef,
  as: Component = 'div',
  ...props
}: ScrollTopAnchorProps<C> & ComponentProps<C>) => {
  return (
    <Component
      id={anchorId ?? DEFAULT_SCROLL_ANCHOR_ID}
      ref={anchorRef}
      {...props}
    />
  );
};
